import { createContext, type ReactNode, useCallback, useContext, useState } from "react";
import { Badge, Body1, Button, type ButtonProps, Display2, getTokenValue, isWeb, KeyboardAwareScrollView, KeyboardStickyView, useSafeAreaInsets, YStack } from "@medbillai/ui";
import type { ShallowDistributiveOmit } from "@medbillai/utils";
import { BackgroundRadialGrantedGradient } from "../BackgroundRadialGrantedGradient";
import { NewGLogo } from "../local-images";
import { OnboardingProgressHeader } from "./OnboardingProgressHeader";
import { useNativePadding } from "./useNativePadding";

// Resolve at module-load — Tamagui's native `Image` doesn't reliably
// parse `$`-prefixed size tokens, so pass it a concrete number.
const LOGO_SIZE = Number(getTokenValue("$si5", "size"));
const FOOTER_KEYBOARD_OFFSET = Number(getTokenValue("$-sp2", "space"));

// Value/setter split so the Footer (producer) only subscribes to the stable
// setter — publishing a new height via onLayout doesn't loop back into its
// render and re-fire onLayout.
const OnboardingFooterHeightContext = createContext<number>(0);
const OnboardingFooterHeightSetterContext = createContext<(height: number) => void>(() => {});
type TextButtonProps = Extract<ButtonProps, {
  children: ReactNode;
}>;
type OnboardingButtonProps = ShallowDistributiveOmit<TextButtonProps, "variant" | "fillWidth" | "size">;
type OnboardingNavProps = {
  /** 0–100. When omitted, the progress bar is hidden. */
  progress?: number;
  /** Native only — web always shows the wordmark. Pass to render a back button. */
  onBackPress?: () => void;
  /** Accessible title; not rendered visibly. */
  title?: string;
  /** Web only — render the Granted wordmark in the left slot. @default true */
  showLogo?: boolean;
};
type OnboardingHeaderBaseProps = {
  title: string;
  subtitle?: string;
  /** Flex alignment and `textAlign` for title/subtitle. @default "center" */
  alignment?: "center" | "left";
  testID?: string;
};
type OnboardingHeaderProps = OnboardingHeaderBaseProps & ({
  showLogo?: boolean;
  badge?: never;
} | {
  showLogo?: never;
  badge?: string;
});
type OnboardingContentProps = {
  children: ReactNode;
};
type OnboardingFooterProps = {
  /** Web-only fade; matches the opacity/transition pattern used for content. */
  isFading?: boolean;
  children: ReactNode;
};
type OnboardingPrimaryActionProps = OnboardingButtonProps & {
  /** @default "primary" */
  variant?: "primary" | "brand";
};
type OnboardingDisclaimerProps = {
  children: ReactNode;
};

/**
 * Nav slot. Callers should always render `<OnboardingScreen.Nav />` so the
 * slot reserves the same `$si7` vertical space whether or not a progress
 * bar or back button is shown. On web the Granted wordmark renders by
 * default — pass `showLogo={false}` on screens that should suppress it.
 */
function OnboardingNav({
  progress,
  onBackPress,
  title,
  showLogo
}: OnboardingNavProps) {
  return <OnboardingProgressHeader progress={progress} onBackPress={onBackPress} title={title} showLogo={showLogo} data-sentry-element="OnboardingProgressHeader" data-sentry-component="OnboardingNav" data-sentry-source-file="OnboardingScreen.tsx" />;
}

/**
 * Header region. Designed to be rendered as the first child of
 * `<OnboardingScreen.Content>` — horizontal padding and the top inset are
 * owned by `Content`, so rendering `<Header>` outside of `<Content>` is
 * unsupported.
 */
function OnboardingHeader(props: OnboardingHeaderProps) {
  const {
    title,
    subtitle,
    alignment = "center",
    testID
  } = props;
  const isLeft = alignment === "left";
  const textAlign = isLeft ? "left" : "center";
  return <YStack paddingBottom="$sp8" gap="$sp1.5" alignItems={isLeft ? "flex-start" : "center"} testID={testID} data-sentry-element="YStack" data-sentry-component="OnboardingHeader" data-sentry-source-file="OnboardingScreen.tsx">
      {props.showLogo ? <NewGLogo height={LOGO_SIZE} width={LOGO_SIZE} /> : null}
      {props.badge ? <Badge status="positive" variant="filled" size="medium">
          {props.badge}
        </Badge> : null}
      <Display2 textAlign={textAlign} data-sentry-element="Display2" data-sentry-source-file="OnboardingScreen.tsx">{title}</Display2>
      {subtitle ? <Body1 color="$textSubtle" textAlign={textAlign}>
          {subtitle}
        </Body1> : null}
    </YStack>;
}

/**
 * Content region. Owns the scroll region and body padding. On web, the
 * outer wrapper IS the scroll region (short content sits at natural size,
 * long content shrinks and scrolls internally so the footer stays pinned).
 * On native, wraps a `ScrollView` that only scrolls when content overflows.
 */
function OnboardingContent({
  children
}: OnboardingContentProps) {
  if (isWeb) {
    return <YStack width="100%" maxWidth={360} alignSelf="center" maxHeight="100%" style={{
      overflowY: "auto"
    }} flexShrink={1} flexBasis="auto" minHeight={0}>
        <ContentBody>{children}</ContentBody>
      </YStack>;
  }
  return <NativeContentScrollRegion data-sentry-element="NativeContentScrollRegion" data-sentry-component="OnboardingContent" data-sentry-source-file="OnboardingScreen.tsx">{children}</NativeContentScrollRegion>;
}
function ContentBody({
  children
}: {
  children: ReactNode;
}) {
  return <YStack flex={1} paddingTop="$sp4" paddingBottom="$sp6" paddingHorizontal="$sp2.5" data-sentry-element="YStack" data-sentry-component="ContentBody" data-sentry-source-file="OnboardingScreen.tsx">
      {children}
    </YStack>;
}
function NativeContentScrollRegion({
  children
}: {
  children: ReactNode;
}) {
  const footerHeight = useContext(OnboardingFooterHeightContext);
  const insets = useSafeAreaInsets();

  // KAS computes scroll using useWindowDimensions().height (full-screen, incl.
  // top inset / Dynamic Island), so its "visible area above keyboard" is taller
  // than the actual app area by `insets.top`. Add the inset back so the offset
  // expresses the gap from keyboard top to where the input must clear; also add
  // |FOOTER_KEYBOARD_OFFSET| since KeyboardStickyView lifts the Footer that gap
  // *above* the keyboard top.
  const bottomOffset = footerHeight + insets.top - FOOTER_KEYBOARD_OFFSET;
  return <KeyboardAwareScrollView style={{
    flex: 1
  }} bottomOffset={bottomOffset} bounces={false} overScrollMode="never" contentContainerStyle={{
    flexGrow: 1
  }} data-sentry-element="KeyboardAwareScrollView" data-sentry-component="NativeContentScrollRegion" data-sentry-source-file="OnboardingScreen.tsx">
      <ContentBody data-sentry-element="ContentBody" data-sentry-source-file="OnboardingScreen.tsx">{children}</ContentBody>
    </KeyboardAwareScrollView>;
}

/**
 * Footer region. Renders children inside the shared action-strip container
 * (paddings, gap, fade-on-transition). Children should be
 * `<PrimaryAction>`, optional `<SecondaryAction>` / `<TertiaryAction>`, and
 * optional `<Disclaimer>`, in that order — each self-renders its own
 * button/wrapper. On web the strip is constrained to the 360px column; on
 * native it's wrapped in `KeyboardStickyView` so it stays flush with the
 * keyboard top.
 */
function OnboardingFooter({
  children,
  isFading
}: OnboardingFooterProps) {
  const setFooterHeight = useContext(OnboardingFooterHeightSetterContext);
  const strip = <YStack paddingHorizontal="$sp2.5" gap="$sp2" opacity={isFading ? 0 : 1} transition="300ms ease">
      {children}
    </YStack>;
  if (isWeb) {
    return <YStack width="100%" maxWidth={360} alignSelf="center">
        {strip}
      </YStack>;
  }
  return <KeyboardStickyView offset={{
    opened: FOOTER_KEYBOARD_OFFSET
  }} data-sentry-element="KeyboardStickyView" data-sentry-component="OnboardingFooter" data-sentry-source-file="OnboardingScreen.tsx">
      <YStack onLayout={event => setFooterHeight(event.nativeEvent.layout.height)} data-sentry-element="YStack" data-sentry-source-file="OnboardingScreen.tsx">
        {strip}
      </YStack>
    </KeyboardStickyView>;
}
function OnboardingPrimaryAction({
  variant = "primary",
  ...rest
}: OnboardingPrimaryActionProps) {
  return <Button variant={variant} size="large" fillWidth {...rest} data-sentry-element="Button" data-sentry-component="OnboardingPrimaryAction" data-sentry-source-file="OnboardingScreen.tsx" />;
}
function OnboardingSecondaryAction(props: OnboardingButtonProps) {
  return <Button variant="secondary" size="large" fillWidth {...props} data-sentry-element="Button" data-sentry-component="OnboardingSecondaryAction" data-sentry-source-file="OnboardingScreen.tsx" />;
}
function OnboardingTertiaryAction(props: OnboardingButtonProps) {
  return <Button variant="tertiary" size="large" fillWidth {...props} data-sentry-element="Button" data-sentry-component="OnboardingTertiaryAction" data-sentry-source-file="OnboardingScreen.tsx" />;
}
function OnboardingDisclaimer({
  children
}: OnboardingDisclaimerProps) {
  return <YStack alignItems="center" data-sentry-element="YStack" data-sentry-component="OnboardingDisclaimer" data-sentry-source-file="OnboardingScreen.tsx">{children}</YStack>;
}
export type OnboardingScreenProps = {
  /** Render `BackgroundRadialGrantedGradient` fullscreen behind the content. */
  showBackgroundGradient?: boolean;
  children: ReactNode;
};
function OnboardingScreenRoot({
  showBackgroundGradient,
  children
}: OnboardingScreenProps) {
  // Safe-area padding for native (all zeros on web). Bottom inset collapses
  // to 0 while the keyboard is visible so the KeyboardStickyView'd footer
  // stays flush with the keyboard top; Tamagui's `transition` animates that
  // change.
  const padding = useNativePadding();
  const [footerHeight, setFooterHeight] = useState(0);
  // Stable setter + sub-pixel dedupe avoids re-render ping-pong from onLayout.
  const setHeight = useCallback((next: number) => {
    setFooterHeight(prev => Math.abs(prev - next) < 0.5 ? prev : next);
  }, []);
  return <OnboardingFooterHeightContext.Provider value={footerHeight} data-sentry-element="OnboardingFooterHeightContext.Provider" data-sentry-component="OnboardingScreenRoot" data-sentry-source-file="OnboardingScreen.tsx">
      <OnboardingFooterHeightSetterContext.Provider value={setHeight} data-sentry-element="OnboardingFooterHeightSetterContext.Provider" data-sentry-source-file="OnboardingScreen.tsx">
        <YStack flex={1} transition="200ms easeCubic" {...padding} {...isWeb ? {
        paddingTop: "$sp2"
      } : {}} data-sentry-element="YStack" data-sentry-source-file="OnboardingScreen.tsx">
          {showBackgroundGradient ? <BackgroundRadialGrantedGradient fullscreen /> : null}
          {children}
        </YStack>
      </OnboardingFooterHeightSetterContext.Provider>
    </OnboardingFooterHeightContext.Provider>;
}
export const OnboardingScreen = Object.assign(OnboardingScreenRoot, {
  Nav: OnboardingNav,
  Header: OnboardingHeader,
  Content: OnboardingContent,
  Footer: OnboardingFooter,
  PrimaryAction: OnboardingPrimaryAction,
  SecondaryAction: OnboardingSecondaryAction,
  TertiaryAction: OnboardingTertiaryAction,
  Disclaimer: OnboardingDisclaimer
});
export type { OnboardingHeaderProps, OnboardingNavProps, OnboardingPrimaryActionProps };